import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { DrawerProvider } from './drawer/DrawerProvider';
import { DrawerFrame } from './DrawerFrame';
import { DrawerContent } from './drawer/DrawerContent';
import { ShellHeader } from './ShellHeader';
import { JourneyContent } from '../journeys/JourneyContent';
// Direct path, not the journeys barrel ('../journeys'): the barrel re-exports
// JourneyProvider → journey manifest → renderers, which forms an import cycle.
import { useResolvedJourney } from '../journeys/useResolvedJourney';
import { SurveyIframeOverlay } from '../liveAgent/SurveyIframeOverlay';
// The orchestrator API; setHistoryCleared retires the "new chat started" notice on unmount,
// useAssistantStore/closeSurveyOverlay drive the post-chat survey takeover (WS-11.1).
import { useAssistant, useAssistantStore } from '../orchestrator/AssistantOrchestrator';

const Content = ({ drawerState, isResolving, header, body }) => {
  if (isResolving) return null;
  return (
    <>
      {drawerState === 'OPEN' && header}
      {body}
    </>
  );
};

Content.propTypes = {
  drawerState: PropTypes.string.isRequired,
  isResolving: PropTypes.bool.isRequired,
  header: PropTypes.node,
  body: PropTypes.node,
};

// The ONE shared frame instance for every journey, filled with whatever the resolved journey
// declares. See .claude/context/shell-composition.md.
export const AssistantShell = ({
  drawerState, handleDrawerStateChange, offsetMargin, handleClose, journeyId
}) => {
  // Same useBreakpoint('lg') the frame and App's FAB read — cheaper than threading isMobile
  // back out of the frame as a render prop.
  const { lesser: isMobile } = useBreakpoint('lg');
  const { journey, isResolving } = useResolvedJourney(journeyId);

  // Header slot: shown only when fully open. See .claude/context/shell-composition.md.
  const CustomHeader = journey.header;
  const header = CustomHeader
    ? <CustomHeader onClose={handleClose} isMobile={isMobile} />
    : <ShellHeader onClose={handleClose} isMobile={isMobile} />;

  const surveyOverlayUrl = useAssistantStore((state) => state.surveyOverlayUrl);
  const { closeSurveyOverlay, setHistoryCleared } = useAssistant();

  // The store outlives this Shell; App unmounts it at CLOSED, so unmount retires the notice.
  useEffect(() => () => setHistoryCleared?.(false), [setHistoryCleared]);

  // Body slot: see .claude/context/shell-composition.md.
  const body = journey.bareContent ? (
    <JourneyContent />
  ) : (
    <DrawerContent
      overlay={surveyOverlayUrl ? (
        <SurveyIframeOverlay surveyUrl={surveyOverlayUrl} onClose={closeSurveyOverlay} />
      ) : null}
    >
      <div className="sui-flex-grow sui-border-none sui-min-h-0 sui-relative">
        <JourneyContent />
      </div>
    </DrawerContent>
  );

  return (
    <ErrorBoundary name="magic-apron-assistant-with-portal">
      <DrawerProvider drawerState={drawerState}>
        <DrawerFrame
          drawerState={drawerState}
          setDrawerState={handleDrawerStateChange}
          offsetMargin={offsetMargin}
        >
          <Content
            header={header}
            body={body}
            drawerState={drawerState}
            isResolving={isResolving}
          />
        </DrawerFrame>
      </DrawerProvider>
    </ErrorBoundary>
  );
};

// No `.dataModel` here — see .claude/context/shell-composition.md.

AssistantShell.propTypes = {
  drawerState: PropTypes.string.isRequired,
  handleDrawerStateChange: PropTypes.func.isRequired,
  offsetMargin: PropTypes.number.isRequired,
  handleClose: PropTypes.func.isRequired,
  journeyId: PropTypes.string,
};
